import React, { useEffect, useState } from "react";
import { Card, Dropdown, Grid, Label } from "semantic-ui-react";
import LuxonDataTable from "../../components/LuxonDataTable";
import { formatDateTime } from "../../utils/datetimeUtils";
import InputSearch from "../../components/InputSearch";
import PageHeader from "../../components/PageHeader";
import { getProspectsAppointments } from "../../services/prospects.service";
import { PROSPECTS_STATUSES } from "../../utils/constants";
import { toast } from "react-toastify";
import RootTemplate from "../../components/RootTemplate";
import ProspectAppointment, { ProspectAppointmentStatus } from "../../types/entities/ProspectAppointment";

const CompletedList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [search, setSearch] = useState("");
    const [statusSelected, setStatusSelected] = useState("");

    const columns = [
        {
            name: "Fecha en que se completo",
            selector: (row: ProspectAppointment) => formatDateTime(row.prospect.saleCompletedDate),
        },
        {
            name: "Nombre del prospecto",
            selector: (row: ProspectAppointment) => row.prospect.person?.fullName || '',
        },
        {
            name: "Producto",
            selector: (row: ProspectAppointment) => row.prospect.product.name,
        },
        {
            name: "Estatus",
            selector: (row: ProspectAppointment) => getStatusLabel(row.status),
        },
    ];

    const getStatusLabel = (status: ProspectAppointmentStatus) => (
        <Label color={PROSPECTS_STATUSES[status].color}>{PROSPECTS_STATUSES[status].name}</Label>
    );

    const fetchData = async (page: number, search?: string, statusSelected?: string) => {
        setLoading(true);
        try {
            const status = statusSelected || "SALE_CONCRETED,SALE_NOT_CONCRETED,COMPLETED";
            const response = await getProspectsAppointments(perPage, page - 1, "createdAt,desc", search, status);
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
        } catch (error) {
            toast.error("Error al obtener la informacion");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        fetchData(page, search, statusSelected);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        try {
            const status = statusSelected || "SALE_CONCRETED,SALE_NOT_CONCRETED,COMPLETED";
            const response = await getProspectsAppointments(newPerPage, page - 1, "createdAt,desc", search, status);
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
            setPerPage(newPerPage);
        } catch (error) {
            toast.error("Error al obtener la informacion");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1, search, statusSelected);
        // eslint-disable-next-line
    }, [search, statusSelected]);

    const completedStatuses = [
        { key: 'SALE_CONCRETED', text: 'Venta concretada', value: 'SALE_CONCRETED' },
        { key: 'SALE_NOT_CONCRETED', text: 'Venta no concretada', value: 'SALE_NOT_CONCRETED' },
        { key: 'COMPLETED', text: 'Cita completada', value: 'COMPLETED' },
    ];

    const handleStatusChange = (status: string) => {
        setStatusSelected(status);
        fetchData(1, search, status);
    };

    return (
        <RootTemplate>
            <PageHeader title="Prospectos completados" subtitle="Clientes potenciales" />
            <Grid width={16}>
                <Grid.Column width={8}>
                    <InputSearch icon="search" placeholder="Búsqueda por nombre de prospecto..." setSearch={setSearch} />
                </Grid.Column>
                <Grid.Column width={8}>
                    <Dropdown
                        placeholder="Selecciona un estatus"
                        fluid
                        selection
                        value={statusSelected}
                        options={completedStatuses}
                        onChange={(e, data) => handleStatusChange(data.value as string)}
                    />
                </Grid.Column>
            </Grid>
            <Card fluid>
                <LuxonDataTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    totalRows={totalRows}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                />
            </Card>
        </RootTemplate>
    );
};

export default CompletedList;
